import React, { useEffect, ReactElement, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';

import api from '~/services/api';
import Loader from '~/ui/components/common/Loader';
import SelectComponent from '~/ui/components/inputs/SelectWithoutAnimation';
import Input from '~/ui/components/inputs/Input';
import Button from '~/ui/components/common/Button';
import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';
import formatDictionaryOptions from '~/utils/formatDictionaryOptions';
import { deEscalationValidationSchema } from './validates';

import { IDeEscalationInitialValues, IRequestInfo } from './types';
import { IDictionaryTypes } from '~/services/api/dictionaries/types';
import { IOption } from '~/types';
import { IDetails } from '../types';
import styles from './Styles.module.scss';

interface IProps {
  showSecondSubmit: boolean;
  setModalTitle: (v: string) => void;
  setDetails: (v: IDetails) => void;
  defaultValues: IDeEscalationInitialValues;
  details: IDetails | null;
  requestInfo: IRequestInfo;
}

const AddDeEscalation = ({
  showSecondSubmit,
  setModalTitle,
  setDetails,
  defaultValues,
  details,
  requestInfo,
}: IProps): ReactElement => {
  const [deEscalations, setDeEscalations] = useState<IOption[]>([] as IOption[]);
  const [loading, setLoading] = useState(false);

  const {
    reset,
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = useForm({ defaultValues, resolver: deEscalationValidationSchema });

  const setCurrent = useStoreActions(actions => actions.clientDetails.setCurrent);
  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);
  const { onUpdateDeEscalation, onCreateDeEscalation } = useStoreActions(
    actions => actions.clientDetails,
  );

  const onMount = useCallback(async () => {
    try {
      const deEscalationDictionaries = await api.dictionaries
        .getAvailableTypeList(IDictionaryTypes.DeEscalationTechnique)
        .then(r => formatDictionaryOptions(r.data));

      setDeEscalations(deEscalationDictionaries);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  const onSubmit = async (vals: IDeEscalationInitialValues, evt: React.BaseSyntheticEvent) => {
    const { name } = (evt as React.BaseSyntheticEvent<HTMLFormElement>).nativeEvent.submitter;
    try {
      if (details && details.id) {
        const payload = {
          requestInfo: { ...requestInfo, id: String(details.id) },
          requestPayload: vals,
        };
        onUpdateDeEscalation(payload);
        setDetails(null);
        setCurrent(null);
      } else {
        const payload = {
          requestInfo,
          requestPayload: vals,
        };

        await onCreateDeEscalation(payload);

        reset({ id: null, note: '' });
      }
      setModalTitle(name);
      const type = details?.id ? 'update' : 'added';
      showNotify({ message: `De-Escalation technique successfully ${type}` });
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  if (loading) return <Loader />;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <SelectComponent
            isRelativeWindow
            name="id"
            options={deEscalations}
            control={control}
            errors={errors}
            maxMenuHeight={150}
            label="Select De-escalation Technique"
            isDisabled={!showSecondSubmit}
            hideSelectedOptions={false}
          />
        </Grid>
        <Grid item sm={12}>
          <Input
            name="note"
            register={register}
            label="De-escalation Technique Note"
            multiline
            errors={errors}
          />
        </Grid>
      </Grid>
      <div className={styles.buttonsWrapper}>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => {
            setDetails(null);
            setModalTitle(null);
            setCurrent(null);
          }}
        >
          Cancel
        </Button>
        {showSecondSubmit && (
          <Button
            color="primary"
            variant="outlined"
            type="submit"
            name="Add De-escalation Technique"
            className={styles.margin}
          >
            Save and Add another
          </Button>
        )}
        <Button
          color="primary"
          variant="contained"
          type="submit"
          name={null}
          className={!showSecondSubmit ? styles.margin : ''}
        >
          {showSecondSubmit ? 'Save' : 'Update'}
        </Button>
      </div>
    </form>
  );
};

export default AddDeEscalation;
